import React from 'react'

import { FormattedTime } from 'react-intl'

import isEmpty from 'lodash-es/isEmpty'
import isFinite from 'lodash-es/isFinite'

import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import T from 'components/FormattedMessage'

import classNames from 'classnames'
import formatMatchMinute from 'lib/formatMatchMinute'
import { MatchEventToken } from 'lib/enumTranslations'

import { IMatchEvent, IPlayer, ITeam } from 'protos'

import Jersey from 'components/Jersey'

interface Props {
  type?: IMatchEvent['type']
  name?: IPlayer['name']
  value?: IMatchEvent['displayValue']
  clock?: IMatchEvent['clock']
  jersey?: IPlayer['jersey'] | ITeam['jersey']
  onClick?: () => void,
  reverse?: boolean
  timestamp?: IMatchEvent['timestamp']
}

const styles = (theme: Theme) => createStyles({
  minuteContainer: {
    minWidth: 64,
  },
  minute: {
    zIndex: 2,
    padding: '7px',
    minWidth: 32,
    textAlign: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
  },
  minuteEmpty: {
    minWidth: 16,
  },
  clickable: {
    cursor: 'pointer',
  },
})

type InjectedProps = Props & WithStyles<typeof styles>

const TimelineItem: React.SFC<InjectedProps> = ({
  type,
  name,
  value,
  clock,
  jersey,
  classes,
  onClick,
  reverse,
  timestamp,
}) => {
  const isShowValue = isFinite(value) && value !== 0
  const isShowEventType = isFinite(type)
  const isShowName = !isEmpty(name)
  const isValuePositive = isShowValue && value! > 0

  return (
    <Grid
      wrap="nowrap"
      onClick={onClick}
      direction={reverse ? 'row-reverse' : 'row'}
      alignItems="center"
      className={classNames({ [classes.clickable]: !!onClick })}
      container
    >
      <Grid xs item zeroMinWidth>
        <Grid direction="column" alignItems={reverse ? 'flex-start' : 'flex-end'} container>
          <Grid item>
            {!jersey && timestamp && (
              <Typography variant="caption" noWrap>
                <FormattedTime value={+timestamp * 1000} />
              </Typography>
            )}
            {jersey && (
              <Jersey jersey={jersey} />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid className={classes.minuteContainer} item>
        <Grid alignItems="center" justify="center" direction="row" container>
          <Grid className={classNames(classes.minute, { [classes.minuteEmpty]: !clock })} item>
            <Typography variant="caption" noWrap>
              {clock && formatMatchMinute(clock)}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid xs item zeroMinWidth>
        <Grid direction="column" alignItems={reverse ? 'flex-end' : 'flex-start'} container>
          {(isShowValue || isShowEventType) && (
            <Grid item>
              <Grid direction={reverse ? 'row-reverse' : 'row'} spacing={1} wrap="nowrap" container>
                {isShowEventType && (
                  <Grid item>
                    <Typography variant="caption" noWrap>
                      <T id={MatchEventToken(type!)} />
                    </Typography>
                  </Grid>
                )}
                {isShowValue && (
                  <Grid item>
                    <Typography color={isValuePositive ? 'primary' : 'error'} variant="caption" noWrap>
                      {isValuePositive ? `+${value}` : `${value}`}
                      <T id="common_unit_point" />
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
          {isShowName && (
            <Grid item>
              <Typography variant="h3" noWrap>{name}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default withStyles(styles)(TimelineItem)
